import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import EmptyState from './components/EmptyState';
import { Collapsible, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import {
  DATA_ROOM_RFI_STATUS,
  DataRoomRFI,
  DataRoomRFIStatus,
  FileTypeRFI,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { AddDocToDataRoomModal } from './components/AddDocToDataRoomModal';
import useCallbackRef from 'hooks/useCallbackRef';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import AddAllDocsToDataRoomModal from './components/AddAllDocsToDataRoomModal';
import RfiInstance from './components/RfiInstance';
import RfiDetailsDrawer from './components/RfiDetailsDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import RfiAnalytics from './components/RfiAnalytics';
import RfiTemplatesTable from './components/RfiTemplatesTable';
import CreateRfiActions from './components/CreateRfiActions';

import './RfiList.styles.scss';

type RfiListProps = {
  className?: string;
};

const RfiList: React.FC<RfiListProps> = (props) => {
  const { className } = props;

  const [selectedRfiFile, setSelectedRfiFile] = useState<FileTypeRFI>();

  const [selectedRfiId, setSelectedRfiId] = useState('');
  const [selectedRfiCategoryId, setSelectedRfiCategoryId] = useState('');

  const [addToDRModal, addToDRModalRef] = useCallbackRef<ModalRef>(null);
  const [addAllToDRModal, addAllToDRModalRef] = useCallbackRef<ModalRef>(null);
  const [detailsDrawer, detailsDrawerRef] = useCallbackRef<SideDrawerRef>(null);

  const {
    isLoading,
    pending,
    submitted,
    closed,
    updateRfi,
    updateRfiFile,
    updateRfiFiles,
    getRfiById,
  } = useContext(RfiContext);

  const rfiDetails = useMemo(
    () => getRfiById(selectedRfiId),
    [selectedRfiId, getRfiById],
  );

  const { t } = useTranslation();

  const classes = classNames('rfi-list', 'white-panel', className);

  const showEmptyPage = useMemo(
    () => !isLoading && !pending.length && !submitted.length && !closed.length,
    [isLoading, pending, submitted, closed],
  );

  const renderRfiTrigger = useCallback(
    (label: string, status: DataRoomRFIStatus, count: number) => (
      <div className="trigger">
        <p className={classNames(`rfi-color--${status}`)}>
          {!count ? label : `${label} (${count})`}
        </p>
        <ChevrondownIcon />
      </div>
    ),
    [],
  );

  const onAddSuccess = useCallback(
    (file: FileTypeRFI) => updateRfiFile(selectedRfiId, file),
    [selectedRfiId, updateRfiFile],
  );

  const onAddAllSuccess = useCallback(
    (files: FileTypeRFI[]) => updateRfiFiles(selectedRfiId, files),
    [selectedRfiId, updateRfiFiles],
  );

  const RenderItem = useCallback(
    (item: DataRoomRFI) => (
      <RfiInstance
        key={item._id}
        dataRoomRfi={item}
        onAddFileToDr={(categoryId: string, file: FileTypeRFI) => {
          setSelectedRfiId(item._id);
          setSelectedRfiCategoryId(categoryId);
          setSelectedRfiFile(file);
          addToDRModal.open();
        }}
        onAddAllFilesToDr={() => {
          setSelectedRfiId(item._id);
          addAllToDRModal?.open();
        }}
        onSeeMore={() => {
          setSelectedRfiId(item._id);
          detailsDrawer?.setOpen(true);
        }}
      />
    ),
    [addToDRModal, addAllToDRModal, detailsDrawer],
  );

  return (
    <div className={classes}>
      {isLoading ? (
        <Loader overlay={false} />
      ) : showEmptyPage ? (
        <EmptyState />
      ) : (
        <>
          <div className="panel-title">
            <h3>{t('existingRequests')}</h3>

            <CreateRfiActions />
          </div>

          <RfiAnalytics />

          {!!pending.length && (
            <Collapsible
              className="white-panel rfi-item"
              trigger={renderRfiTrigger(
                t('pending'),
                DATA_ROOM_RFI_STATUS.PENDING,
                pending.length,
              )}
            >
              {pending.map(RenderItem)}
            </Collapsible>
          )}

          {!!submitted.length && (
            <Collapsible
              defaultOpen
              className="white-panel rfi-item"
              trigger={renderRfiTrigger(
                t('submitted'),
                DATA_ROOM_RFI_STATUS.SUBMITTED,
                submitted.length,
              )}
            >
              {submitted.map(RenderItem)}
            </Collapsible>
          )}

          {!!closed.length && (
            <Collapsible
              className="white-panel rfi-item"
              trigger={renderRfiTrigger(
                t('closed'),
                DATA_ROOM_RFI_STATUS.CLOSED,
                closed.length,
              )}
            >
              {closed.map(RenderItem)}
            </Collapsible>
          )}

          <RfiTemplatesTable />
        </>
      )}

      <RfiDetailsDrawer
        ref={detailsDrawerRef}
        rfi={rfiDetails}
        onRfiUpdate={updateRfi}
      />

      <AddDocToDataRoomModal
        ref={addToDRModalRef}
        dataRoomRfiCategoryId={selectedRfiCategoryId}
        document={selectedRfiFile}
        onAddSuccess={onAddSuccess}
      />
      <AddAllDocsToDataRoomModal
        ref={addAllToDRModalRef}
        dataRoomRfiId={selectedRfiId}
        onAddAllSuccess={onAddAllSuccess}
      />
    </div>
  );
};

export default RfiList;
